import React from 'react'
import { ArrowLeft, Home, ChevronRight, User, Shield } from 'lucide-react'
import { useAuth } from '../hooks/useAuth'

interface PathBreadcrumbProps {
  currentPath: string
  onNavigate: (path: string) => void
  onNavigateUp: () => void
}

const PathBreadcrumb: React.FC<PathBreadcrumbProps> = ({
  currentPath,
  onNavigate,
  onNavigateUp
}) => {
  const pathParts = currentPath.split('/').filter(Boolean)
  const { user } = useAuth()

  return (
    <div className="bg-white dark:bg-[#2a2a2a] rounded-lg shadow-custom-light p-4 mb-4">
      <div className="flex items-center space-x-2 text-[var(--color-primary)] overflow-x-auto">
        {user && (
          <div className="flex items-center text-[var(--color-secondary)] font-medium px-2 py-1 bg-[var(--color-background)] dark:bg-[#1f1f1f] rounded-md">
            {user.isAdmin ? (
              <Shield size={16} className="mr-1.5" />
            ) : (
              <User size={16} className="mr-1.5" />
            )}
            <span className="truncate max-w-[120px] sm:max-w-none">{user.username}</span>
          </div>
        )}
        
        <button
          onClick={() => onNavigate('/')}
          className="flex items-center hover:bg-[var(--color-background)] dark:hover:bg-[#1f1f1f] p-1.5 rounded-md transition-colors"
          title="Root directory"
        >
          <Home size={18} />
        </button>
        
        {pathParts.length > 0 && (
          <ChevronRight size={16} className="text-[var(--color-accent)]" />
        )}
        
        {pathParts.map((part, index) => (
          <React.Fragment key={index}>
            <button
              onClick={() => {
                const newPath = '/' + pathParts.slice(0, index + 1).join('/')
                onNavigate(newPath)
              }}
              className="hover:bg-[var(--color-background)] dark:hover:bg-[#1f1f1f] p-1.5 rounded-md transition-colors truncate max-w-[150px] sm:max-w-[200px] md:max-w-none"
              title={part}
            >
              {part}
            </button>
            {index < pathParts.length - 1 && (
              <ChevronRight size={16} className="text-[var(--color-accent)]" />
            )}
          </React.Fragment>
        ))}
        
        {currentPath !== '/' && (
          <button
            onClick={onNavigateUp}
            className="ml-2 p-1.5 rounded-md hover:bg-[var(--color-background)] dark:hover:bg-[#1f1f1f] transition-colors"
            aria-label="Go up"
            title="Go up one level"
          >
            <ArrowLeft size={18} />
          </button>
        )}
      </div>
    </div>
  )
}

export default PathBreadcrumb